/**
 * UserRegisterContainer
 * --
 */
import React from 'react';
import UserRegisterPresenter from './UserRegisterPresenter';
import { useCreateUser } from 'hooks/UserHooks';
import { useDuplicateCheck } from 'hooks/AuthHooks';
import MessageAlert from 'utils';
import { useGetGrades } from 'hooks/GradeHooks';
import validateCheck from 'utils/Validation';
import dayjs from 'dayjs';

// 회원 정보
const INITIAL_VALUE = {
  user_account: '',
  password: '',
  user_name: '',
  user_phone: '',
  user_email: '',
  user_gender: 'M',
  postcode: '',
  user_address: '',
  user_address_detail: '',
  user_birthday: '',
  grade_id: '',
  memo: '',
};

// 약관
const INITIAL_CHECK = {
  terms_of_use: false, // 이용약관 동의
  terms_of_privacy: false, // 개인정보 수집 및 이용 동의
  terms_of_marketing: false, // 마케팅 목적의 개인정보 수집 및 이용 동의
  terms_of_ad: false, // 혜택 및 광고성 정보 수신 동의
  terms_of_age: false, // 만 14세 이상입니다
  marketing_consent: false, // 마케팅정보 수집 및 활용 동의
};

// 중복검사 여부
const INITIAL_DUPLICATE = {
  user_account: false,
  user_phone: false,
  user_email: false,
};

const UserRegisterContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { onClose } = props;

  /* ===== Data ===== */
  const { data: grades } = useGetGrades(null, {
    select: (res) => {
      const { status, data } = res;

      if (status !== 200) return [];

      return data;
    },
    initialData: [],
  });

  /* ===== Mutate ===== */
  const { createUser } = useCreateUser();
  const { duplicate } = useDuplicateCheck();

  /* ===== STATE ===== */
  const [inputValue, setInputValue] = React.useState(INITIAL_VALUE);
  const [privacy, setPrivacy] = React.useState(INITIAL_CHECK);
  const [isLoading, setIsLoading] = React.useState(false);
  const [duplicateCheck, setDuplicateCheck] = React.useState(INITIAL_DUPLICATE);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 데이터 바인딩
   * --
   * @param {string} t
   * @param {string} v
   */
  const handleChange = (t, v) => {
    setInputValue((prev) => ({
      ...prev,
      [t]: v,
    }));
  };

  /**
   * 회원 생성
   * --
   */
  const handleSubmit = async () => {
    let isSuccess = false;

    if (!handleValidateCheck()) return;

    try {
      setIsLoading(true);

      const userData = {
        ...inputValue,
        user_birthday: dayjs(inputValue?.user_birthday).unix(),
        user_setting: {
          ...privacy,
        },
      };

      const { status } = await createUser(userData);

      if (status !== 200) {
        MessageAlert.info('회원 생성에 실패하였습니다.');
        return;
      }

      MessageAlert.success('회원이 성공적으로 생성되었습니다.');
      isSuccess = true;
    } catch (err) {
      console.error(`[handleSubmit] Error!! ${err}`);
      MessageAlert.error('회원생성에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
        if (isSuccess) {
          setInputValue(INITIAL_VALUE);
          setDuplicateCheck(INITIAL_DUPLICATE);
          setPrivacy(INITIAL_CHECK);
        }
      }, 1000);
    }
  };

  /**
   * 중복검사
   * --
   * @param {string} t
   */
  const handleDuplicateCheck = async (t) => {
    try {
      setIsLoading(true);

      const type = {
        user_account: '아이디',
        user_phone: '휴대폰 번호',
        user_email: '이메일',
      };

      if (t === 'user_email' && !validateCheck('email', inputValue[t])) {
        MessageAlert.info('이메일형식이 아닙니다.');
        return false;
      }

      const { data } = await duplicate({ [t]: inputValue[t], type: 'USER' });

      if (!data) {
        MessageAlert.warning(`사용할 수 없는 ${type[t]}입니다.`);
        return;
      }

      const confirm = window.confirm(
        `사용가능한 ${type[t]}입니다. 사용하시겠습니까?`
      );

      if (!confirm) return;

      setDuplicateCheck((prev) => ({
        ...prev,
        [t]: true,
      }));
    } catch (err) {
      console.log(`[handleDuplicateCheck] Error!! ${err}`);
      console.error('중복검사에 실패하였습니다!');
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 체크박스 핸들러
   * --
   */
  const handleChecked = (t, c) => {
    setPrivacy((prev) => ({
      ...prev,
      [t]: c,
    }));
  };

  /**
   * 회원생성 유효성 검사
   * --
   * @returns {boolean}
   */
  const handleValidateCheck = () => {
    const {
      grade_id,
      user_account,
      password,
      user_name,
      user_phone,
      user_birthday,
      user_email,
    } = inputValue;

    const {
      user_account: account_check,
      user_phone: phone_check,
      user_email: email_check,
    } = duplicateCheck;

    const { terms_of_use, terms_of_privacy, terms_of_age } = privacy;

    if (!grade_id) {
      MessageAlert.info('회원 등급을 선택해주세요.');
      return false;
    }

    if (!user_account) {
      MessageAlert.info('아이디를 입력해주세요.');
      return false;
    }

    if (!user_name) {
      MessageAlert.info('이름을 입력해주세요.');
      return false;
    }

    if (!account_check) {
      MessageAlert.info('아이디 중복검사를 실행해주세요.');
      return false;
    }

    if (!password) {
      MessageAlert.info('비밀번호를 입력해주세요.');
      return false;
    }

    if (!user_phone || user_phone.length < 12) {
      MessageAlert.info('연락처를 입력해주세요.');
      return false;
    }

    if (!phone_check) {
      MessageAlert.info('휴대폰 중복검사를 실행해주세요.');
      return false;
    }

    if (!user_birthday) {
      MessageAlert.info('생년월일을 선택해주세요.');
      return false;
    }

    if (!user_email) {
      MessageAlert.info('이메일을 입력해주세요.');
      return false;
    }

    if (!validateCheck('email', user_email)) {
      MessageAlert.info('이메일형식이 아닙니다.');
      return false;
    }

    if (!email_check) {
      MessageAlert.info('이메일 중복검사를 실행해주세요.');
      return false;
    }

    if (!terms_of_use || !terms_of_privacy || !terms_of_age) {
      MessageAlert.info('필수 동의사항을 체크해주세요.');
      return false;
    }

    return true;
  };

  /* ===== HOOKS ===== */
  /* ===== RENDER ===== */
  return (
    <>
      <UserRegisterPresenter
        inputValue={inputValue}
        isLoading={isLoading}
        grades={grades}
        privacy={privacy}
        onChange={handleChange}
        onSubmit={handleSubmit}
        onDuplicateCheck={handleDuplicateCheck}
        onChecked={handleChecked}
        onClose={onClose}
        duplicateCheck={duplicateCheck}
      />
    </>
  );
};

export default UserRegisterContainer;
